// src/components/maps/MASTERMap.jsx
// ─────────────────────────────────────────────────────────────────────────────
// MASTERMap, unified geo-intelligence engine
//
// Supports three modes (auto-detected from props):
//   • Venue-only    , pass `venues` array (backwards compatible)
//   • Supplier-only , pass `items` with type:"vendor"
//   • Mixed         , pass `items` with venues + vendors → filter bar shown
//
// Features:
//   • Dark CARTO tiles, full-bleed right layout
//   • Gold filled pins for venues
//   • Category-colour outline pins for suppliers
//   • Internal filter bar (All / Venues / per-category), mixed data only
//   • Ghost opacity (0.12) on non-active filter categories
//   • Nominatim geocoding with session cache
//   • Popup: venues → price/capacity · suppliers → rating/category
//   • PinSyncBus bidirectional sync (card hover ↔ pin highlight)
//   • Follow mode: map pans with active card (toggle to free)
//   • Viewport continuity across filter changes and re-renders
//   • Staggered pin entrance animation
//   • Left-edge gradient fade, absorbed into layout
//   • Analytics: pin_click, map_viewport, map_geocode_miss → trackEvent
//   • Aura annotation overlay (bottom-left glass panel)

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { geocodeLocation } from "../../utils/geocoding/geocodeLocation";
import { PinSyncBus } from "./PinSyncBus";
import { trackEvent } from "../../lib/tracker";
import { listingProfileUrl } from "../../utils/listingUrl";

const NU   = "var(--font-body)";
const GOLD         = "#d39a21";
const VENDOR_GREEN = "#4a9e6b";  // vendor pin colour, clearly distinct from venue gold

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

// ── Country default map centres ───────────────────────────────────────────────
const DEFAULT_CENTRES = {
  italy:    [42.5,  12.5],
  england:  [52.5,  -1.5],
  uk:       [54.0,  -2.5],
  france:   [46.5,   2.3],
  usa:      [39.5, -98.35],
  spain:    [40.4,  -3.7],
  greece:   [39.0,  22.0],
  portugal: [39.5,  -8.0],
  germany:  [51.2,  10.5],
  ireland:  [53.3,  -8.0],
};

// ── Category colour palette, venues gold, vendors green ─────────────────────
const PIN_COLOURS = {
  "wedding-venues":   GOLD,          // gold, primary venue product
  "wedding-planners": VENDOR_GREEN,
  "photographers":    VENDOR_GREEN,
  "videographers":    VENDOR_GREEN,
  "florists":         VENDOR_GREEN,
  "caterers":         VENDOR_GREEN,
  "musicians":        VENDOR_GREEN,
  "hair-beauty":      VENDOR_GREEN,
  "bridal-boutiques": VENDOR_GREEN,
  "wedding-vendors":  VENDOR_GREEN,  // umbrella fallback
};

const PIN_LABELS = {
  "wedding-venues":   "Venues",
  "photographers":    "Photographers",
  "wedding-planners": "Planners",
  "florists":         "Florists",
  "videographers":    "Videographers",
  "caterers":         "Caterers",
  "musicians":        "Musicians",
  "hair-beauty":      "Hair & Beauty",
  "bridal-boutiques": "Bridal Boutiques",
  "wedding-vendors":  "Vendors",
};

// CTA label per category, shown on the map popup button
const VIEW_LABELS = {
  "wedding-venues":   "View Venue",
  "wedding-planners": "View Planner",
  "photographers":    "View Photographer",
  "videographers":    "View Videographer",
  "florists":         "View Florist",
  "caterers":         "View Caterer",
  "musicians":        "View Musician",
  "hair-beauty":      "View Profile",
  "bridal-boutiques": "View Boutique",
};

// ── Leaflet singleton loader ──────────────────────────────────────────────────
let leafletPromise = null;
function loadLeaflet() {
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve) => {
    if (window.L) { resolve(window.L); return; }
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet"; link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.onload = () => resolve(window.L);
    document.head.appendChild(script);
  });
  return leafletPromise;
}


// ── Pin icon factory ──────────────────────────────────────────────────────────
// Premium teardrop, rounded head, short softly curved point.
// 3-layer structure: outer ring → main body → inner core circle.
// Hierarchy by size only (same design, different scale):
//   standard: 20px, featured: 26px, showcase/editors: 32px
// state:   "standard" | "featured" | "showcase" | "hover" | "active" | "ghost"
// color:   hex string (defaults to GOLD)
// variant: "filled" (venues) | "outline" (suppliers)
function makePinIcon(L, state = "standard", size = 20, color = GOLD, variant = "filled") {
  // Size hierarchy, state drives scale
  const tierSize = state === "showcase" ? 32
    : state === "featured" ? 26
    : size;
  const w = tierSize;
  const h = Math.round(w * 1.375);  // compact ratio, point ≈40-50% of head

  const isGhost  = state === "ghost";
  const isHover  = state === "hover";
  const isActive = state === "active";
  const hoverScale = isActive ? 1.15 : isHover ? 1.12 : 1;
  const pinColor   = isGhost ? `rgba(211,154,33,0.35)` : color;

  // Outer ring, thin stroke on all pins, stronger for featured/showcase
  const outerOpacity = state === "showcase" ? 0.5
    : state === "featured" ? 0.4
    : 0.3;
  const outerStroke = state === "showcase" ? 1.4
    : state === "featured" ? 1.2
    : 0.9;
  const outerColor = state === "showcase" ? "#E8E0D0" : pinColor;

  // Glow filter for hover/active
  const glowId = `lwd-glow-${Math.random().toString(36).slice(2, 6)}`;
  const glowFilter = (isActive || isHover)
    ? `<defs><filter id="${glowId}" x="-50%" y="-40%" width="200%" height="200%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="${isActive ? 2.5 : 1.8}" result="blur"/>
        <feFlood flood-color="${pinColor}" flood-opacity="${isActive ? 0.4 : 0.25}"/>
        <feComposite in2="blur" operator="in" result="glow"/>
        <feMerge><feMergeNode in="glow"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter></defs>`
    : "";
  const filterAttr = (isActive || isHover) ? `filter="url(#${glowId})"` : "";

  // Pin body: filled (venues) vs outline (suppliers)
  const pathFill    = variant === "outline" ? "rgba(10,8,6,0.82)" : pinColor;
  const pathStroke  = variant === "outline" ? pinColor : "none";
  const pathSW      = variant === "outline" ? "1.4" : "0";
  const pathOpacity = isGhost ? 0.4 : 0.92;

  // Core circle radius, ~1/3 of head
  const coreR = w * 0.14;

  // Rendered size with hover scale
  const rw = w * hoverScale;
  const rh = h * hoverScale;

  // Teardrop path using viewBox 0 0 32 44 (normalised), scaled to w×h
  return L.divIcon({
    className: `lwd-mmap-pin lwd-mmap-pin--${state}`,
    iconSize:    [rw, rh],
    iconAnchor:  [rw / 2, rh],
    popupAnchor: [0, -(rh - 4)],
    html: `
      <svg viewBox="0 0 32 44" width="${rw}" height="${rh}"
        xmlns="http://www.w3.org/2000/svg" style="overflow:visible;display:block;
        transition:transform 0.22s cubic-bezier(0.34,1.4,0.64,1);
        transform-origin:center bottom;">
        ${glowFilter}
        <g ${filterAttr}>
          <path d="M16 2C8.3 2 2 8.3 2 16c0 4.5 2.2 8.5 5.5 11L16 40c0 0 8.5-13 8.5-13 3.3-2.5 5.5-6.5 5.5-11C30 8.3 23.7 2 16 2z"
            fill="none" stroke="${outerColor}" stroke-width="${outerStroke}" opacity="${outerOpacity}"/>
          <path d="M16 3.5C9.1 3.5 3.5 9.1 3.5 16c0 4 1.9 7.6 4.9 9.8L16 37.5l7.6-11.7c3-2.2 4.9-5.8 4.9-9.8C28.5 9.1 22.9 3.5 16 3.5z"
            fill="${pathFill}" stroke="${pathStroke}" stroke-width="${pathSW}" opacity="${pathOpacity}"/>
          <circle cx="16" cy="15.5" r="${coreR / w * 32}" fill="rgba(255,255,255,0.92)"/>
        </g>
      </svg>`,
  });
}

// ── Listing URL builder, mirrors LuxuryVendorCard profileUrl logic ──────────
// Delegates to the canonical URL service (src/utils/listingUrl); '#' keeps the
// popup anchor inert rather than pointing at a broken route.
function buildListingUrl(v) {
  return listingProfileUrl(v) || "#";
}

// ── Popup builders ────────────────────────────────────────────────────────────
function buildVenuePopup(v, accentColor = GOLD) {
  const _i0 = Array.isArray(v.imgs) ? v.imgs[0] : null;
  const img = v.thumbnail || (typeof _i0 === "string" ? _i0 : (_i0?.src || _i0?.url || "")) || "";
  const price    = v.priceFrom ? `From ${v.priceFrom}` : "";
  const loc      = [v.city, v.region].filter(Boolean).join(", ");
  const cap      = v.capacity ? ` · ${v.capacity} guests` : "";
  const offerHtml = v.hasOffer && v.offerLabel
    ? `<div style="font-size:10px;color:${accentColor};margin-bottom:6px;opacity:0.82">✦ ${v.offerLabel}</div>`
    : "";
  const imgHtml = img
    ? `<div style="width:100%;height:88px;overflow:hidden;border-radius:5px 5px 0 0;
        margin:-10px -10px 10px;width:calc(100% + 20px);background:#111">
        <img src="${img}" alt="${v.name}"
          style="width:100%;height:88px;object-fit:cover;display:block" loading="lazy"/>
      </div>`
    : "";

  return `
    <div style="font-family:var(--font-body);width:220px;padding:10px">
      ${imgHtml}
      <div style="font-weight:700;font-size:13px;color:#fff;margin-bottom:3px;line-height:1.3">
        ${v.name}
      </div>
      <div style="font-size:10px;color:rgba(255,255,255,0.42);margin-bottom:4px">
        ${loc}${cap}
      </div>
      ${price
        ? `<div style="font-size:12px;color:${accentColor};font-weight:600;margin-bottom:6px">${price}</div>`
        : ""}
      ${offerHtml}
      <div style="margin-top:8px">
        <a href="${buildListingUrl(v)}"
          style="display:block;text-align:center;text-decoration:none;
            font-family:var(--font-body);font-size:9px;font-weight:700;
            letter-spacing:0.14em;text-transform:uppercase;
            padding:6px 12px;border-radius:4px;
            border:1px solid rgba(211,154,33,0.7);
            background:rgba(211,154,33,0.12);color:${GOLD}">
          View Venue
        </a>
      </div>
    </div>`;
}

function buildSupplierPopup(v, accentColor = GOLD) {
  const _si0 = Array.isArray(v.imgs) ? v.imgs[0] : null;
  const img = v.thumbnail || (typeof _si0 === "string" ? _si0 : (_si0?.src || _si0?.url || "")) || "";
  const loc       = [v.city, v.region].filter(Boolean).join(", ");
  const catLabel  = PIN_LABELS[v.category] || v.category || "Supplier";
  const ratingHtml = v.rating
    ? `<div style="font-size:10px;color:${accentColor};margin-bottom:4px;opacity:0.85">
        ${"★".repeat(Math.round(v.rating))}${"☆".repeat(5 - Math.round(v.rating))}
        <span style="opacity:0.55;margin-left:4px">${v.rating.toFixed(1)}</span>
       </div>`
    : "";
  const tierHtml = v.tier
    ? `<div style="font-size:9px;color:${accentColor};opacity:0.6;margin-bottom:6px;
        letter-spacing:0.1em;text-transform:uppercase">${v.tier}</div>`
    : "";
  const imgHtml = img
    ? `<div style="width:100%;height:88px;overflow:hidden;border-radius:5px 5px 0 0;
        margin:-10px -10px 10px;width:calc(100% + 20px);background:#111">
        <img src="${img}" alt="${v.name}"
          style="width:100%;height:88px;object-fit:cover;display:block" loading="lazy"/>
      </div>`
    : "";

  return `
    <div style="font-family:var(--font-body);width:220px;padding:10px">
      ${imgHtml}
      <div style="font-size:8px;letter-spacing:0.2em;text-transform:uppercase;
        color:${accentColor};opacity:0.6;margin-bottom:4px">${catLabel}</div>
      <div style="font-weight:700;font-size:13px;color:#fff;margin-bottom:3px;line-height:1.3">
        ${v.name}
      </div>
      <div style="font-size:10px;color:rgba(255,255,255,0.42);margin-bottom:4px">${loc}</div>
      ${ratingHtml}
      ${tierHtml}
      <div style="margin-top:8px">
        <a href="${buildListingUrl(v)}"
          style="display:block;text-align:center;text-decoration:none;
            font-family:var(--font-body);font-size:9px;font-weight:700;
            letter-spacing:0.14em;text-transform:uppercase;
            padding:6px 12px;border-radius:4px;
            border:1px solid ${accentColor}55;
            background:${accentColor}18;color:${accentColor}">
          ${VIEW_LABELS[v.category] || "View Profile"}
        </a>
      </div>
    </div>`;
}

function buildPopupHtml(item) {
  const color = PIN_COLOURS[item.category] || GOLD;
  return item.type === "vendor"
    ? buildSupplierPopup(item, color)
    : buildVenuePopup(item, GOLD);
}

// ═════════════════════════════════════════════════════════════════════════════
export default function MASTERMap({
  venues       = [],         // backwards compat, venue array
  items        = null,       // mixed array: [{ ...venueOrVendor, type, category }]
  label        = "Venue Map",
  viewMode     = "grid",     // "grid" | "list", drives toggle button label
  onToggleView,              // fn(), called when user clicks back to Grid/List
  countrySlug  = "italy",
  pageBg       = "#f2f0ea",  // match left column bg for gradient fade
  auraSummary  = null,       // Aura annotation string
  activeFilter = null,       // category slug driven by Aura, "photographers" | "wedding-venues" | null
  onFilterChange,            // fn(categorySlug), fires when user manually changes filter pill
}) {
  const mapElRef       = useRef(null);
  const mapRef         = useRef(null);
  const markersRef     = useRef([]);   // [{ id, marker, state, lat, lng, category, type, item }]
  const popupRef       = useRef(null);
  const viewportRef    = useRef(null); // { center, zoom }, persisted across filter changes
  const geocodingActive = useRef(false);

  const [ready,      setReady]      = useState(false);
  const [followMode, setFollowMode] = useState(true);
  const [pinFilter,  setPinFilter]  = useState(activeFilter || "all");

  // ── Sync internal filter when Aura drives activeFilter ────────────────────
  useEffect(() => {
    setPinFilter(activeFilter || "all");
  }, [activeFilter]);

  // ── Normalise data ─────────────────────────────────────────────────────────
  // Accept either `items` (new unified model) or `venues` (backwards compat)
  const allItems = useMemo(() => {
    if (items && items.length > 0) return items;
    return venues.map((v) => ({ ...v, type: "venue", category: v.category || "wedding-venues" }));
  }, [items, venues]);

  // Categories present in the data (for filter bar)
  const presentCategories = useMemo(() => {
    const seen = new Set();
    const result = [];
    allItems.forEach((i) => {
      const cat = i.category || "wedding-venues";
      if (!seen.has(cat)) { seen.add(cat); result.push(cat); }
    });
    return result;
  }, [allItems]);

  const hasSuppliers = useMemo(
    () => presentCategories.some((c) => c !== "wedding-venues"),
    [presentCategories]
  );

  const showFilterBar = hasSuppliers; // Only show filter when mixed data

  // ── Init Leaflet map (once per mount) ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let rafId = null;

    loadLeaflet().then((L) => {
      if (cancelled || !mapElRef.current || mapRef.current) return;

      // Defer init by one animation frame so the browser completes layout.
      // When loadLeaflet() resolves from cache it fires as a microtask, before
      // the browser has measured the flex container, giving Leaflet a 0-height
      // element. rAF guarantees the container has real dimensions.
      rafId = requestAnimationFrame(() => {
        if (cancelled || !mapElRef.current || mapRef.current) return;

        const centre = DEFAULT_CENTRES[countrySlug] || [42.5, 12.5];
        const map = L.map(mapElRef.current, {
          zoomControl:        false,
          scrollWheelZoom:    true,
          attributionControl: false,
        }).setView(centre, 7);

        L.control.zoom({ position: "topleft" }).addTo(map);
        // Light basemap, matching the destinations map (GlobalHeatMap, /all-destinations),
        // which is the reference the listing map is meant to look like. Both now render the
        // same CARTO rastertiles/voyager style; this was dark_all, so the listing map read
        // black against a light destinations map.
        L.tileLayer(`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${import.meta.env.VITE_CARTO_API_KEY}`, {
          maxZoom: 19, subdomains: "abcd",
        }).addTo(map);

        // Nudge Leaflet to measure the real container size after tiles are added
        requestAnimationFrame(() => {
          if (mapRef.current) mapRef.current.invalidateSize();
        });

        // Persist viewport + analytics
        let _vpTimer = null;
        map.on("moveend zoomend", () => {
          viewportRef.current = { center: map.getCenter(), zoom: map.getZoom() };
          clearTimeout(_vpTimer);
          _vpTimer = setTimeout(() => {
            const c = map.getCenter();
            const b = map.getBounds();
            trackEvent("map_viewport", {
              zoom:       map.getZoom(),
              center_lat: Math.round(c.lat * 1000) / 1000,
              center_lng: Math.round(c.lng * 1000) / 1000,
              bounds_sw:  `${b.getSouthWest().lat.toFixed(3)},${b.getSouthWest().lng.toFixed(3)}`,
              bounds_ne:  `${b.getNorthEast().lat.toFixed(3)},${b.getNorthEast().lng.toFixed(3)}`,
              country:    countrySlug,
            });
          }, 1500);
        });

        mapRef.current = map;
        setReady(true);
      });
    });

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      setReady(false);
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      markersRef.current = [];
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Place / refresh pins when allItems or ready changes ───────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const L = window.L;
    if (!L) return;

    markersRef.current.forEach(({ marker }) => marker.remove());
    markersRef.current = [];
    if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }

    const withCoords    = allItems.filter((v) => v.lat && v.lng);
    const needsGeocoding = allItems.filter((v) => !v.lat || !v.lng);
    const bounds        = [];

    withCoords.forEach((v, i) => {
      setTimeout(() => {
        if (!mapRef.current) return;
        _addPin(L, map, v);
      }, i * 20);
      bounds.push([v.lat, v.lng]);
    });

    if (bounds.length > 0) {
      if (viewportRef.current) {
        map.setView(viewportRef.current.center, viewportRef.current.zoom, { animate: false });
      } else {
        map.fitBounds(bounds, { padding: [44, 44], maxZoom: 8, animate: true });
      }
    }

    if (needsGeocoding.length > 0 && !geocodingActive.current) {
      geocodingActive.current = true;
      _geocodeProgressively(L, map, needsGeocoding).finally(() => {
        geocodingActive.current = false;
      });
    }
  }, [allItems, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply category filter, ghost non-matching pins ───────────────────────
  useEffect(() => {
    markersRef.current.forEach(({ marker, category }) => {
      const el = marker.getElement();
      if (!el) return;
      const matches = pinFilter === "all" || pinFilter === category;
      el.style.opacity    = matches ? "1" : "0.12";
      el.style.transition = "opacity 0.2s ease";
      el.style.pointerEvents = matches ? "auto" : "none";
    });
  }, [pinFilter]);

  // ── Add a single pin ──────────────────────────────────────────────────────
  function _addPin(L, map, v, forceState) {
    const isVenue   = v.type !== "vendor";
    const category  = v.category || "wedding-venues";
    const color     = PIN_COLOURS[category] || GOLD;
    const variant   = isVenue ? "filled" : "outline";
    const pinState  = forceState || (
      v.isShowcase ? "showcase" :
      v.isFeatured ? "featured" : "standard"
    );

    const icon = makePinIcon(L, pinState, 20, color, variant);
    const marker = L.marker([v.lat, v.lng], {
      icon,
      zIndexOffset: v.isFeatured || v.isShowcase ? 200 : 0,
    }).addTo(map);

    marker.on("mouseover", () => {
      PinSyncBus.emit("pin:hover", v.id);
      _openPopup(map, marker, v);
    });
    marker.on("mouseout", () => {
      PinSyncBus.emit("pin:leave", v.id);
    });
    marker.on("click", () => {
      PinSyncBus.emit("pin:click", v.id);
      trackEvent("pin_click", {
        item_id:       v.id,
        item_name:     v.name,
        item_slug:     v.slug,
        item_type:     v.type || "venue",
        item_category: category,
        country:       countrySlug,
      });
    });

    markersRef.current.push({
      id: v.id, marker, state: pinState,
      lat: v.lat, lng: v.lng,
      category, type: v.type || "venue", item: v,
    });

    // Apply current filter immediately (in case filter was set before this pin was added)
    const el = marker.getElement();
    if (el) {
      const matches = pinFilter === "all" || pinFilter === category;
      el.style.opacity       = matches ? "1" : "0.12";
      el.style.pointerEvents = matches ? "auto" : "none";
    }

    return marker;
  }

  // ── Open popup ────────────────────────────────────────────────────────────
  function _openPopup(map, marker, v) {
    if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
    const popup = window.L.popup({
      className:   "lwd-mmap-popup",
      maxWidth:    240,
      closeButton: false,
      autoPan:     false,
      offset:      [0, -6],
    })
      .setContent(buildPopupHtml(v))
      .setLatLng(marker.getLatLng());
    popup.addTo(map);
    popupRef.current = popup;
  }

  // ── Geocode items progressively ───────────────────────────────────────────
  async function _geocodeProgressively(L, map, needsGeo) {
    for (const v of needsGeo) {
      if (!mapRef.current) break;
      const result = await geocodeLocation(v);
      if (!result || !mapRef.current) {
        if (!result) {
          trackEvent("map_geocode_miss", {
            item_id:       v.id,
            item_name:     v.name,
            item_category: v.category || "wedding-venues",
            country:       countrySlug,
          });
        }
        continue;
      }
      _addPin(L, map, { ...v, lat: result.lat, lng: result.lng });
    }
  }

  // ── Highlight a pin ───────────────────────────────────────────────────────
  const _highlightPin = useCallback((id, active, hover = false) => {
    const L = window.L;
    if (!L || !mapRef.current) return;
    markersRef.current.forEach(({ id: pid, marker, state, category, type }) => {
      const isTarget = pid === id;
      const color    = PIN_COLOURS[category] || GOLD;
      const variant  = type === "vendor" ? "outline" : "filled";

      // Determine pin state: active > hover > restore original
      let pinState = state;
      let zIndex = 0;

      if (isTarget) {
        if (active) {
          pinState = "active";
          zIndex = 500;
        } else if (hover) {
          pinState = "hover";
          zIndex = 300;
        }
      }

      marker.setIcon(makePinIcon(L, pinState, 20, color, variant));
      marker.setZIndexOffset(zIndex);
    });
  }, []);

  // ── Pan map to a pin ──────────────────────────────────────────────────────
  const _panToPin = useCallback((id) => {
    const entry = markersRef.current.find((m) => m.id === id);
    if (entry && mapRef.current) {
      mapRef.current.panTo([entry.lat, entry.lng], { animate: true, duration: 0.4 });
    }
  }, []);

  // ── PinSyncBus listeners ──────────────────────────────────────────────────
  useEffect(() => {
    const offCardHover = PinSyncBus.on("card:hover", (id) => {
      _highlightPin(id, true, false); // active=true, hover=false
      const entry = markersRef.current.find((m) => m.id === id);
      if (entry && mapRef.current) {
        // Auto-zoom to pin on hover, zoom in but never below current zoom
        const targetZoom = Math.max(mapRef.current.getZoom(), 10);
        mapRef.current.setView([entry.lat, entry.lng], targetZoom, { animate: true, duration: 0.5 });
        _openPopup(mapRef.current, entry.marker, entry.item);
      }
    });

    const offCardLeave = PinSyncBus.on("card:leave", (id) => {
      _highlightPin(id, false, false); // active=false, hover=false
      if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
    });

    const offCardClick = PinSyncBus.on("card:click", (id) => {
      _highlightPin(id, true, false); // active=true, hover=false
      _panToPin(id);
      if (mapRef.current) {
        const entry = markersRef.current.find((m) => m.id === id);
        if (entry) {
          mapRef.current.setView(
            [entry.lat, entry.lng],
            Math.max(mapRef.current.getZoom(), 11),
            { animate: true, duration: 0.5 }
          );
        }
      }
    });

    // Pin hover events (direct pin interaction)
    const offPinHover = PinSyncBus.on("pin:hover", (id) => {
      _highlightPin(id, false, true); // active=false, hover=true
    });

    const offPinLeave = PinSyncBus.on("pin:leave", (id) => {
      _highlightPin(id, false, false); // active=false, hover=false
    });

    return () => { offCardHover(); offCardLeave(); offCardClick(); offPinHover(); offPinLeave(); };
  }, [followMode, _highlightPin, _panToPin]);

  // ── Filter bar pill renderer ──────────────────────────────────────────────
  function FilterPill({ cat, label: pillLabel, color }) {
    const isActive = pinFilter === cat;
    return (
      <button
        onClick={() => {
          const next = isActive ? "all" : cat;
          setPinFilter(next);
          onFilterChange?.(next === "all" ? null : cat);
        }}
        style={{
          display:        "flex",
          alignItems:     "center",
          gap:            5,
          fontFamily:     NU,
          fontSize:       9,
          fontWeight:     600,
          letterSpacing:  "0.12em",
          textTransform:  "uppercase",
          color:          isActive ? color : "rgba(255,255,255,0.38)",
          background:     isActive ? `${color}18` : "rgba(8,6,4,0.65)",
          backdropFilter: "blur(8px)",
          border:         `1px solid ${isActive ? `${color}60` : "rgba(255,255,255,0.1)"}`,
          borderRadius:   6,
          padding:        "5px 9px",
          cursor:         "pointer",
          transition:     "all 0.2s ease",
          whiteSpace:     "nowrap",
        }}
      >
        <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: isActive ? color : "rgba(255,255,255,0.2)",
          flexShrink: 0,
          transition: "background 0.2s",
        }} />
        {pillLabel}
      </button>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  const zoomTopOffset = showFilterBar ? 96 : 52;

  return (
    <div
      aria-label="Map"
      style={{
        position:     "relative",
        width:        "100%",
        height:       "100%",
        minHeight:    480,
        // Matches Voyager's land tone, the same value and the same reason as GlobalHeatMap:
        // a dark backdrop would frame the light tiles and flash black while they load.
        background:   "#f4f1ec",
        borderRadius: 0,
        overflow:     "hidden",
      }}
    >
      {/* Left-edge gradient fade */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: 52,
          background: `linear-gradient(to right, ${pageBg}, transparent)`,
          zIndex: 400, pointerEvents: "none",
        }}
      />

      {/* Leaflet map canvas */}
      <div ref={mapElRef} style={{ width: "100%", height: "100%" }} />

      {/* Loading overlay */}
      {!ready && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(10,8,6,0.92)", zIndex: 10,
        }}>
          <span style={{
            fontFamily: NU, fontSize: 11, letterSpacing: "0.25em",
            textTransform: "uppercase", color: "rgba(255,255,255,0.25)",
          }}>
            Loading map…
          </span>
        </div>
      )}

      {/* ── Map header: label + controls ── */}
      <div style={{
        position: "absolute", top: 14, left: 62, right: 14,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        zIndex: 500, userSelect: "none",
      }}>
        <span style={{
          fontFamily: NU, fontSize: 9, letterSpacing: "0.38em",
          textTransform: "uppercase", color: "rgba(255,255,255,0.28)",
          pointerEvents: "none",
        }}>
          {label}
        </span>

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            onClick={() => setFollowMode((f) => !f)}
            title={followMode ? "Following active card" : "Free navigation"}
            style={{
              fontFamily: NU, fontSize: 9, fontWeight: 600,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color:      followMode ? GOLD : "rgba(255,255,255,0.3)",
              background: followMode ? "rgba(211,154,33,0.1)" : "rgba(8,6,4,0.65)",
              backdropFilter: "blur(8px)",
              border:     `1px solid ${followMode ? "rgba(211,154,33,0.32)" : "rgba(255,255,255,0.1)"}`,
              borderRadius: 6, padding: "5px 10px",
              cursor: "pointer", transition: "all 0.25s",
            }}
          >
            {followMode ? "● Follow" : "○ Free"}
          </button>

          {onToggleView && (
            <button
              onClick={onToggleView}
              style={{
                fontFamily: NU, fontSize: 9, fontWeight: 600,
                letterSpacing: "0.15em", textTransform: "uppercase",
                color: GOLD,
                background: "rgba(8,6,4,0.75)", backdropFilter: "blur(8px)",
                border: "1px solid rgba(211,154,33,0.25)", borderRadius: 6,
                padding: "5px 10px", cursor: "pointer", transition: "all 0.25s",
              }}
            >
              {viewMode === "grid" ? "⊞ Grid" : "≡ List"}
            </button>
          )}
        </div>
      </div>

      {/* ── Category filter bar (mixed data only) ── */}
      {showFilterBar && (
        <div style={{
          position:   "absolute",
          top:        42,
          left:       62,
          right:      14,
          zIndex:     500,
          display:    "flex",
          gap:        5,
          alignItems: "center",
          flexWrap:   "nowrap",
          overflowX:  "auto",
          paddingBottom: 2,
          scrollbarWidth: "none",
        }}>
          {/* All pill */}
          <button
            onClick={() => { setPinFilter("all"); onFilterChange?.(null); }}
            style={{
              fontFamily:     NU,
              fontSize:       9,
              fontWeight:     600,
              letterSpacing:  "0.12em",
              textTransform:  "uppercase",
              color:          pinFilter === "all" ? GOLD : "rgba(255,255,255,0.38)",
              background:     pinFilter === "all" ? "rgba(211,154,33,0.12)" : "rgba(8,6,4,0.65)",
              backdropFilter: "blur(8px)",
              border:         `1px solid ${pinFilter === "all" ? "rgba(211,154,33,0.5)" : "rgba(255,255,255,0.1)"}`,
              borderRadius:   6,
              padding:        "5px 10px",
              cursor:         "pointer",
              transition:     "all 0.2s ease",
              whiteSpace:     "nowrap",
              flexShrink:     0,
            }}
          >
            All
          </button>

          {/* Per-category pills, only categories present in data */}
          {presentCategories.map((cat) => (
            <FilterPill
              key={cat}
              cat={cat}
              label={PIN_LABELS[cat] || cat}
              color={PIN_COLOURS[cat] || GOLD}
            />
          ))}
        </div>
      )}

      {/* Aura annotation, bottom-left glass panel */}
      {auraSummary && (
        <div style={{
          position:       "absolute",
          bottom:         20,
          left:           62,
          maxWidth:       280,
          zIndex:         500,
          background:     "rgba(10,8,6,0.82)",
          backdropFilter: "blur(12px)",
          border:         "1px solid rgba(211,154,33,0.22)",
          borderLeft:     "3px solid rgba(211,154,33,0.7)",
          borderRadius:   "0 6px 6px 0",
          padding:        "8px 12px",
          pointerEvents:  "none",
        }}>
          <div style={{
            fontFamily: NU, fontSize: 8, fontWeight: 700,
            letterSpacing: "0.22em", textTransform: "uppercase",
            color: GOLD, opacity: 0.72, marginBottom: 4,
          }}>
            ✦ Aura
          </div>
          <div style={{
            fontFamily: NU, fontSize: 11, fontStyle: "italic",
            color: "rgba(255,255,255,0.62)", lineHeight: 1.5,
          }}>
            {auraSummary}
          </div>
        </div>
      )}

      {/* Popup + pin CSS */}
      <style>{`
        .lwd-mmap-popup .leaflet-popup-content-wrapper {
          background: rgba(10,8,6,0.96) !important;
          border: 1px solid rgba(211,154,33,0.18) !important;
          border-radius: 8px !important;
          box-shadow: 0 12px 48px rgba(0,0,0,0.65) !important;
          backdrop-filter: blur(14px) !important;
          padding: 0 !important;
        }
        .lwd-mmap-popup .leaflet-popup-tip-container { display: none !important; }
        .lwd-mmap-popup .leaflet-popup-content {
          margin: 0 !important; padding: 0 !important; width: auto !important;
        }
        /* Pin state transitions, instant and smooth */
        .lwd-mmap-pin { cursor: pointer; }
        .lwd-mmap-pin { transition: transform 0.12s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.12s ease; }
        .lwd-mmap-pin--hover { transition: transform 0.14s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .lwd-mmap-pin--active { transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.18s ease; }

        /* Pin animations, subtle and luxury */
        @keyframes lwd-pin-hover-pulse {
          0%,100% { transform: scale(1); }
          50%      { transform: scale(1.12); }
        }
        .lwd-mmap-pin--hover svg { animation: lwd-pin-hover-pulse 1.4s ease-in-out infinite; }

        @keyframes lwd-pin-active-pulse {
          0%,100% { transform: scale(1); }
          50%      { transform: scale(1.22); }
        }
        .lwd-mmap-pin--active svg { animation: lwd-pin-active-pulse 1.6s ease-in-out infinite; }

        /* Glow ring animations */
        @keyframes lwd-ring-pulse {
          0%,100% { opacity: 0.3; transform: scale(1); }
          50%      { opacity: 0.15; transform: scale(1.25); }
        }
        .lwd-pin-glow-ring { animation: lwd-ring-pulse 1.8s ease-in-out infinite; }

        @keyframes lwd-ring-pulse-active {
          0%,100% { opacity: 0.5; transform: scale(1); }
          50%      { opacity: 0.2; transform: scale(1.35); }
        }
        .lwd-pin-glow-ring-active { animation: lwd-ring-pulse-active 1.5s ease-in-out infinite; }
        .leaflet-control-zoom {
          border: 1px solid rgba(211,154,33,0.2) !important;
          border-radius: 6px !important;
          overflow: hidden;
          margin-top: ${zoomTopOffset}px !important;
          margin-left: 14px !important;
        }
        .leaflet-control-zoom a {
          background: rgba(10,8,6,0.82) !important;
          color: rgba(211,154,33,0.7) !important;
          border-bottom: 1px solid rgba(211,154,33,0.1) !important;
          width: 28px !important; height: 28px !important;
          line-height: 28px !important; font-size: 16px !important;
        }
        .leaflet-control-zoom a:hover {
          background: rgba(211,154,33,0.12) !important;
          color: #d39a21 !important;
        }
        .lwd-mmap-filter-scroll::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
